import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { PostVideo } from "../../../../cores/socialPost/interfaces/postVideo";
import { SocialProviderRules } from "../../../../cores/socialPost/config";
import AreYouSure from "../../AreYouSure";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faCloudUploadAlt,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { VideoBankModal } from "../VideoBankModal";
import { GoogleDriveConnectModal } from "./GoogleDriveConnectModal";
import { GoogleDriveFilePickerModal } from "./GoogleDriveFilePickerModal";
import { fetchGoogleDriveConnection } from "../../../../cores/googleDrive/connectApi";
import { toast } from "react-toastify";
import { VideoPreview } from "../creator/VideoPreview";
import { COLOR_PRIMARY } from "../../../../constants/theme";
import { useGlobalCore } from "../../../../cores/globals";
import { ADD_SOCIAL_VIDEO_MODAL } from "../../../../constants/modal.constant";
import { useMeCore } from "../../../../cores/me";
import { Container, Row, Col, Button } from "reactstrap";
import { getBase64 } from "../../../../cores/utils";
import "./ModernVideoForm.scss";

interface Props {
  singleVideo?: boolean;
}

type VideoSource = "upload" | "drive" | "bank" | null;

export default function ModernVideoForm(props: Props) {
  const { singleVideo = false } = props;
  const intl = useIntl(); 
  const {
    addVideo,
    addVideoDuration,
    addVideoSize,
    video,
    images,
    removeAllImages,
    removePostAllMedia,
    selectedPost,
    socialProviders,
  } = useSocialPostCore();
  const { turnOffLoading, turnOnLoading, turnOffModal, isLoading } = useGlobalCore();
  const { company, companyPlan } = useMeCore();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSource, setSelectedSource] = useState<VideoSource>(null);
  const [showVideoBankModal, setShowVideoBankModal] = useState(false);
  const [showDriveConnectModal, setShowDriveConnectModal] = useState(false);
  const [showDriveFilePicker, setShowDriveFilePicker] = useState(false);

  const maxSizeVideo = useMemo(() => {
    if (socialProviders.length === 0) {
      return 300 * 1024 * 1024;
    }
    const sizes = socialProviders
      .map(({ id }) => SocialProviderRules[id]?.maxVideoSize)
      .filter(size => size !== undefined);
    
    if (sizes.length === 0) {
      return 300 * 1024 * 1024;
    }
    
    return Math.max(...sizes) * 1024 * 1024;
  }, [socialProviders]);

  const removeImages = useCallback(() => {
    removeAllImages();
    if (selectedPost) {
      removePostAllMedia();
    }
  }, [removeAllImages, selectedPost, removePostAllMedia]);

  const handleVideoBankClick = () => {
    setShowVideoBankModal(true);
  };

  const openDriveCard = useCallback(async () => {
    if (!company?.id) return;
    try {
      const connection = await fetchGoogleDriveConnection(company.id);
      if (connection) {
        setShowDriveFilePicker(true);
      } else {
        setShowDriveConnectModal(true);
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({ id: "app.googleDrive.connectError" })
      );
    }
  }, [company?.id, intl]);

  const handleDriveFilesSelected = useCallback(
    async (
      files: Array<{ name: string; mimeType: string; data: string; size?: number }>
    ) => {
      const file = files[0];
      if (!file) return;

      removeImages();

      const postVideo: PostVideo = {
        id: Date.now().toString(),
        name: file.name,
        size: file.size || 0,
        type: file.mimeType,
        data: file.data,
        lastModified: Date.now(),
        duration: 0,
        width: 0,
        height: 0,
      };

      addVideo(postVideo);
    },
    [addVideo, removeImages]
  );

  const handleUploadClick = () => {
    // Open file browser directly
    setSelectedSource('upload');
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []).filter(file => 
      file.type.startsWith('video/')
    );
    
    if (files.length > 0) {
      const file = files[0];
      const LARGE_VIDEO_BYTES = 8 * 1024 * 1024;
      // Keep File for direct S3 upload. Only base64-encode small videos for legacy paths.
      const dataUrl =
        file.size > LARGE_VIDEO_BYTES
          ? URL.createObjectURL(file)
          : ((await getBase64(file)) as string);

      const postVideo: PostVideo = {
        id: Date.now().toString(),
        name: file.name,
        size: file.size,
        type: file.type,
        data: dataUrl,
        file,
        lastModified: file.lastModified,
        duration: 0,
        width: 0,
        height: 0,
      };

      addVideo(postVideo);
    }
    
    // Reset input value to allow selecting the same file again
    if (e.target) {
      e.target.value = '';
    }
  };

  const videoSourceOptions = [
    {
      id: "upload" as VideoSource,
      title: intl.formatMessage({ id: "app.socialMediaPost.uploadFromDevice" }),
      description: intl.formatMessage({ id: "app.socialMediaPost.selectFromComputer" }),
      icon: faCloudUploadAlt,
      color: COLOR_PRIMARY,
      action: () => {
        setSelectedSource("upload");
        handleUploadClick();
      },
    },
    {
      id: "drive" as VideoSource,
      title: intl.formatMessage({ id: "app.googleDrive.sourceTitle" }),
      description: intl.formatMessage({ id: "app.googleDrive.videoDescription" }),
      icon: faGoogleDrive,
      color: "#4285F4",
      action: () => {
        setSelectedSource("drive");
        openDriveCard();
      },
    },
    {
      id: "bank" as VideoSource,
      title: intl.formatMessage({ id: "app.socialMediaPost.videoBank" }),
      description: intl.formatMessage({ id: "app.socialMediaPost.freeHighQualityVideos" }),
      icon: faVideo,
      color: '#dc3545',
      action: () => {
        setSelectedSource('bank');
        handleVideoBankClick();
      }
    }
  ];

  return (
    <div className="modern-video-form">
      <Container fluid className="h-100">
        <Row className="h-100">
          <Col xs="12" className="d-flex flex-column h-100">
            {/* Header */}
            <div className="video-form-header text-center py-3">
              <h4>{intl.formatMessage({ id: "app.socialMediaPost.selectVideo" })}</h4>
              <p>{intl.formatMessage({ id: "app.socialMediaPost.chooseHowToAddVideo" })}</p>
            </div>

            {/* Video Source Options */}
            <div className="video-source-options mb-4">
              <Row>
                {videoSourceOptions.map((option) => (
                  <Col xs="12" md="6" key={option.id} className="mb-3">
                    <div
                      className={`video-source-card video-source-card-${option.id} ${selectedSource === option.id ? 'selected' : ''}`}
                      onClick={option.action}
                    >
                      <div className="card-icon">
                        <FontAwesomeIcon 
                          icon={option.icon} 
                          style={{ color: option.color }}
                        />
                      </div>
                      <div className="card-content">
                        <h5>{option.title}</h5>
                        <p>{option.description}</p>
                      </div>
                      {selectedSource === option.id && (
                        <div className="selected-indicator">
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Hidden Native File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple={!singleVideo}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {/* Video Preview Area */}
            <div className="video-preview-section flex-grow-1 d-flex flex-column">
              {(images.length > 0 ||
                (selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE &&
                  selectedPost.media.length > 0)) && (
                <AreYouSure
                  message={intl.formatMessage({ id: "app.socialMediaPost.warningImagesToVideo" })}
                  onClose={() => turnOffModal(ADD_SOCIAL_VIDEO_MODAL)}
                  onSubmit={removeImages}
                />
              )}
              
              {images.length === 0 &&
                (!selectedPost ||
                  selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO ||
                  (selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE &&
                    !selectedPost.media.length)) && (
                  <div className="video-preview-container">
                    {video ? (
                      <div className="video-preview-wrapper">
                        <VideoPreview
                          video={video.data}
                          type={video.type}
                          onLoadedMetadata={(video) => {
                            addVideoDuration(video.duration);
                            addVideoSize(video.videoWidth, video.videoHeight);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="no-video-placeholder">
                        <FontAwesomeIcon
                          icon={faVideo}
                          size="4x"
                          className="mb-3 placeholder-icon"
                        />
                        <h5>{intl.formatMessage({ id: "app.socialMediaPost.notSelectVideo" })}</h5>
                        <p>{intl.formatMessage({ id: "app.socialMediaPost.useOptionsTopAddVideo" })}</p>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Video Bank Modal */}
      <VideoBankModal
        show={showVideoBankModal}
        onHide={() => setShowVideoBankModal(false)}
      />

      {company?.id && (
        <>
          <GoogleDriveConnectModal
            isOpen={showDriveConnectModal}
            toggle={() => setShowDriveConnectModal(false)}
            companyId={company.id}
            onConnected={() => setShowDriveFilePicker(true)}
          />
          <GoogleDriveFilePickerModal
            isOpen={showDriveFilePicker}
            toggle={() => setShowDriveFilePicker(false)}
            companyId={company.id}
            mode="video"
            onFilesSelected={handleDriveFilesSelected}
            onSwitchAccount={() => {
              setShowDriveFilePicker(false);
              setShowDriveConnectModal(true);
            }}
          />
        </>
      )}

    </div>
  );
}
